import dayjs from 'dayjs';

import { daysBetweenYmd, formatDateToDateString, getOperationToday } from 'shared/lib/dateFormatter';

/** Widest window the COMPLETE grid may be read with, in days — the server rejects more (OS-587). */
export const COMPLETE_WINDOW_MAX_DAYS = 365;

/** Grid date columns the window can apply to. Default is ETD, the first one. */
export const COMPLETE_WINDOW_DATE_FIELDS = [
    { value: 'etd', label: 'ETD' },
    { value: 'eta', label: 'ETA' },
    { value: 'feta', label: 'F.ETA' },
];
export const COMPLETE_WINDOW_DEFAULT_FIELD = COMPLETE_WINDOW_DATE_FIELDS[0].value;

/** The window the server uses when the operator has not picked one: the past year up to today. */
export const defaultCompleteWindow = (today = getOperationToday()) => {
    const end = dayjs(today);
    return {
        start: formatDateToDateString(end.subtract(COMPLETE_WINDOW_MAX_DAYS, 'day').toDate()),
        end: formatDateToDateString(end.toDate()),
    };
};

/**
 * Why a picked window cannot be used, or null when it can. Mirrors the server rule so the
 * operator hears about it before the read goes out.
 */
export const completeWindowError = (start, end) => {
    if (!start || !end) return 'Please select both a start and an end date.';
    const span = daysBetweenYmd(start, end);
    if (Number.isNaN(span)) return 'Please enter valid dates.';
    if (span < 0) return 'End date must be on or after the start date.';
    if (span > COMPLETE_WINDOW_MAX_DAYS) return 'Date range cannot exceed 1 year.';
    return null;
};

/** Identity of a window for the read cache; null for the server default. */
export const completeWindowKey = ({ field, start, end } = {}) =>
    start && end ? `${field || COMPLETE_WINDOW_DEFAULT_FIELD}|${start}|${end}` : null;

/**
 * The filter of a COMPLETE read. Without a picked range the server shows the past year,
 * so only the Complete filter is sent. Always an object, because the grid merges it with
 * other read options and a merged list would lose the Complete filter and show In
 * Progress rows instead.
 */
export const buildCompleteReadPayload = ({ field, start, end } = {}) =>
    start && end
        ? {
              case_closure: ['Complete'],
              date_field: field || COMPLETE_WINDOW_DEFAULT_FIELD,
              start_date: start,
              end_date: end,
          }
        : { case_closure: ['Complete'] };

/** Redux state key of the rate/dispute list for a grid, from its main list key (e.g. oimMainUpdateForm → oimRateUpdateForm). */
const siblingStateKey = (mainStateKey, suffix) => mainStateKey.replace(/MainUpdateForm$/, suffix);

const idSet = (rows, pick) => new Set(rows.map((row) => String(pick(row))));
const notIn = (set, pick) => (row) => !set.has(String(pick(row)));

/**
 * The lists a grid keeps in Redux with every Complete shipment, and everything hanging
 * off one (houses, containers, aliases, statuses, rates, comments, disputes), taken out.
 * Returned keyed by server table name so each list can go through the grid's "replace"
 * reducer. Null when the grid holds no Complete shipment.
 *
 * Complete rows are merged into the same lists as In Progress rows, so switching to a
 * new date window has to drop the old window's rows first or the grid would show both.
 */
export const pruneCompleteRows = (state, mainConfig) => {
    const { formState, tableName, keyName } = mainConfig;
    const masters = state[formState[0]] ?? [];
    const removedMasters = idSet(
        masters.filter((row) => row.case_closure === 'Complete'),
        (row) => row.id
    );
    if (removedMasters.size === 0) return null;

    const parentKey = keyName[0];
    const out = { [tableName[0]]: masters.filter(notIn(removedMasters, (row) => row.id)) };

    const houses = state[formState[1]] ?? [];
    const removedHouses = idSet(
        houses.filter((row) => removedMasters.has(String(row[parentKey]))),
        (row) => row.id
    );
    out[tableName[1]] = houses.filter(notIn(removedMasters, (row) => row[parentKey]));

    if (formState[2]) {
        const cntrKey = keyName[1];
        const cntrs = state[formState[2]] ?? [];
        const removedCntrs = idSet(
            cntrs.filter((row) => removedHouses.has(String(row[cntrKey]))),
            (row) => row.id
        );
        out[tableName[2]] = cntrs.filter(notIn(removedHouses, (row) => row[cntrKey]));

        if (formState[3]) {
            const aliasKey = `${tableName[2]}_id`;
            out[tableName[3]] = (state[formState[3]] ?? []).filter(notIn(removedCntrs, (row) => row[aliasKey]));
        }
    }

    out.il_status = (state[formState[4]] ?? []).filter(notIn(removedMasters, (row) => row.parent_id));
    out.il_comment = (state[formState[5]] ?? []).filter(notIn(removedMasters, (row) => row[parentKey]));
    out.il_rate = (state[siblingStateKey(formState[0], 'RateUpdateForm')] ?? []).filter(
        notIn(removedMasters, (row) => row[parentKey])
    );
    out.il_dispute = (state[siblingStateKey(formState[0], 'DisputeUpdateForm')] ?? []).filter(
        notIn(removedMasters, (row) => row[parentKey])
    );
    return out;
};
